import React from 'react';

/**
 * EvolUX Icon Component - New Brand Mark
 * 
 * Features:
 * - Circular arrow representing continuous evolution/improvement
 * - Geometric "X" in the center (matching "UX" in EvolUX)
 * - Blue to turquoise gradient for the arrow
 * - Cyan/turquoise gradient for the X
 * - Clean, minimalist, flat design
 */

// Simple ID generator for unique gradient IDs
let idCounter = 0;
const generateId = () => `evolux-${++idCounter}`;

const EvoluxIcon = ({ size = 32, className = '' }) => {
  // Use a stable unique ID for gradients
  const [uniqueId] = React.useState(() => generateId());
  
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 256 256"
      width={size}
      height={size}
      className={className}
      aria-label="EvolUX Logo"
    >
      <defs>
        {/* Gradient for circular arrow: blue to turquoise */}
        <linearGradient id={`arrow-grad-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#2563EB', stopOpacity: 1 }} />
          <stop offset="60%" style={{ stopColor: '#0EA5E9', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#22F6C7', stopOpacity: 1 }} />
        </linearGradient>
        {/* Gradient for X: turquoise/cyan */}
        <linearGradient id={`x-grad-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#14E0FF', stopOpacity: 1 }} />
          <stop offset="50%" style={{ stopColor: '#22F6C7', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#06B6D4', stopOpacity: 1 }} />
        </linearGradient>
      </defs>
      
      {/* Circular arc (main loop) - clockwise from bottom-right */}
      <path 
        d="M 200 180 A 90 90 0 1 1 200 76"
        fill="none"
        stroke={`url(#arrow-grad-${uniqueId})`}
        strokeWidth="18"
        strokeLinecap="round"
      />
      
      {/* Arrow head at bottom-right */}
      <polygon 
        points="215,195 185,175 200,155"
        fill={`url(#arrow-grad-${uniqueId})`}
      />
      
      {/* Inner X mark - geometric, sharp pointed edges */}
      <g stroke={`url(#x-grad-${uniqueId})`} strokeWidth="16" strokeLinecap="square" fill="none">
        <line x1="90" y1="90" x2="166" y2="166"/>
        <line x1="166" y1="90" x2="90" y2="166"/>
      </g>
    </svg>
  );
};

/**
 * Small version optimized for favicon sizes (16x16, 32x32)
 * Simplified for better legibility at small sizes
 */
export const EvoluxIconSmall = ({ size = 32, className = '' }) => {
  const [uniqueId] = React.useState(() => generateId());
  
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 32 32"
      width={size}
      height={size}
      className={className}
      aria-label="EvolUX Logo"
    >
      <defs>
        <linearGradient id={`arrow-sm-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#2563EB' }} />
          <stop offset="60%" style={{ stopColor: '#0EA5E9' }} />
          <stop offset="100%" style={{ stopColor: '#22F6C7' }} />
        </linearGradient>
        <linearGradient id={`x-sm-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#14E0FF' }} />
          <stop offset="50%" style={{ stopColor: '#22F6C7' }} />
          <stop offset="100%" style={{ stopColor: '#06B6D4' }} />
        </linearGradient>
      </defs>
      
      {/* Circular arc - simplified for small size */}
      <path 
        d="M 25 22.5 A 11.25 11.25 0 1 1 25 9.5"
        fill="none"
        stroke={`url(#arrow-sm-${uniqueId})`}
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      
      {/* Arrow head */}
      <polygon 
        points="27,24.5 23,22 25,19.5"
        fill={`url(#arrow-sm-${uniqueId})`}
      />
      
      {/* Inner X mark */}
      <g stroke={`url(#x-sm-${uniqueId})`} strokeWidth="2.2" strokeLinecap="square" fill="none">
        <line x1="11" y1="11" x2="21" y2="21"/>
        <line x1="21" y1="11" x2="11" y2="21"/>
      </g>
    </svg>
  );
};

/**
 * Full logo with icon + wordmark "EvolUX"
 */
export const EvoluxLogo = ({ iconSize = 28, className = '' }) => {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <EvoluxIconSmall size={iconSize} />
      <span className="text-xl font-bold">
        <span className="text-indigo-600">Evol</span>
        <span className="bg-gradient-to-r from-cyan-500 to-teal-400 bg-clip-text text-transparent">UX</span>
      </span>
    </div>
  );
};

/**
 * Large logo for login/signup pages
 */
export const EvoluxLogoLarge = ({ iconSize = 48, className = '' }) => {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <EvoluxIcon size={iconSize} />
      <div>
        <h1 className="text-3xl font-bold">
          <span className="text-indigo-600">Evol</span>
          <span className="bg-gradient-to-r from-cyan-500 to-teal-400 bg-clip-text text-transparent">UX</span>
        </h1>
        <p className="text-xs text-muted-foreground">User Experience Evolution</p>
      </div>
    </div>
  );
};

export default EvoluxIcon;
